/**
 * recuDEcritureDeForme — la décision du reçu d'une écriture de forme, une fois ses
 * faits réunis : `format_text` (#1863), `format_paragraphs` (#1866) et
 * `copy_format` (#1869) rendent le même reçu, décidé ici.
 *
 * WHY un module pur : les exécuteurs (`formatTextExecutor.ts`,
 * `formatParagraphsExecutor.ts`, `copyFormatExecutor.ts`) parlent à l'éditeur, ce module décide.
 * Ce qui est propre à un verbe — ses réglages jugés, ses champs, ses erreurs —
 * tient dans sa ligne de la table `MOTS` ; tout le reste est commun. Plan refusé après d'autres plans réussis, refus `NO_OP`
 * dont la révision avance quand même, relecture devenue étrangère à l'écriture,
 * révision apparue pendant l'action : ces issues ne se provoquent pas sur
 * l'éditeur réel, elles se jugent donc ici, sur des faits posés à la main.
 */

import {
  CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX,
  CANVAS_FORMAT_QUOTED_TEXT_MAX,
  CANVAS_FORMAT_REFUSAL_DETAIL_MAX,
  CanvasFormatEditorRefusalReasonSchema,
  estUnRefusDeLEditeur,
  CanvasFormatSetAsideReasonSchema,
  type CanvasFormatEditorRefusalReason,
  type CanvasFormatPlace,
  type CanvasFormatSetAsideReason,
  type CanvasFormatSettingReceipt,
  type CanvasFormatSkippedReason,
  type CanvasFormatUnresolvedReason,
  type CanvasFormatWriteReceipt,
  type CanvasTextFormatFilter,
  type CanvasTextFormatSettingKey,
} from '@hector/shared-types';
import {
  abortedBeforeMutation,
  inconclusiveAfterMutation,
  type CanvasActionReceipt,
  type CanvasSnapshotSummary,
  type VerificationCheck,
} from './actionReceipt';
import { direLeLieu } from './horsDuCorps';
import type { ActeVise } from '@hector/docx-forme';
import type { ReglagesDeParagraphe } from './reglagesDeParagraphe';
import type { RaisonDeSaut } from './repliDeLot';
import {
  verifierLaFormeEcrite,
  type EtendueEcrite,
  type PlageDeTexte,
  type ReglagesDeTexte,
  type RelectureDeLActe,
} from './verificationDeForme';
import { copieAuRecu, ditDeLaCopie, erreursDeLaCopie, verificationsDeLaCopie, type FaitsDeLaCopie } from './recuDeCopie';
import type { BilanDeRemiseAZero } from './remiseAZero';
import { verifierLaCopie } from './verificationDeCopie';
import { verifierLaFormeDeParagraphe, verifierLaRemiseAZero, type ParagrapheLaisse } from './verificationDeParagraphe';

/** Les faits d'un verbe, parmi ceux que le reçu commun décide. */
type FaitsDe<Verbe extends FaitsDeLEcritureDeForme['verbe']> = Extract<FaitsDeLEcritureDeForme, { verbe: Verbe }>;

/** Ce que le reçu commun a déjà établi quand il interroge le verbe. */
interface DejaEtabli {
  /** Ce que la relecture juge : des paragraphes, ou des passages visés. */
  etendues: EtendueEcrite[];
  /** Nombre d'éléments à écrire qu'une interruption a laissés non écrits. */
  nonEcrits: number;
  /** Nombre de paragraphes visés qui ne sont pas écrits : l'éditeur les refuse, ou ils sont épargnés. */
  sautes: number;
  laisses: ReadonlyArray<ParagrapheLaisse>;
}

/** Ce que le verbe apporte au reçu commun. Un champ absent : le verbe n'a rien à y dire. */
interface PropreAuVerbe {
  /** Les réglages jugés sur le fichier relu, un reçu par réglage. */
  settings: CanvasFormatSettingReceipt[];
  /** Ce qui est écrit, tel que l'effet le nomme. */
  nomsDesReglages: string;
  /** Les champs de `format` que seul ce verbe rend. */
  champs?: Pick<CanvasFormatWriteReceipt, 'reset' | 'copy'>;
  /** Les erreurs propres au verbe : chaque chose laissée, dite une fois. */
  erreurs?: CanvasActionReceipt['errors'];
  verifications?: VerificationCheck[];
  /** Vrai si le verbe a laissé exprès, sur un paragraphe écrit, une part de ce qu'il devait écrire. */
  laisseDesReglages?: boolean;
  /** Ce que l'effet dit de plus. */
  suiteDeLEffet?: string;
  /** Ce que l'erreur d'une écriture interrompue dit de plus. */
  suiteDeLInterruption?: string;
}

/**
 * Ce qui distingue un verbe dans le reçu commun : ses mots — l'unité d'une
 * écriture (un plan atomique vaut pour tous ses paragraphes ; les écritures
 * hors plan valent une écriture par paragraphe) et l'unité jugée à la
 * relecture —, et ce qui lui est `propre` : ses réglages jugés, leur nom dans
 * l'effet, ses champs, ses erreurs et ses vérifications.
 */
interface MotsDuVerbe<Verbe extends FaitsDeLEcritureDeForme['verbe']> {
  ecritures: string;
  juges: string;
  premierRefus: string;
  /** Un `NO_OP` fait refuser tout un plan de forme de texte : le reçu l'explique. */
  expliqueLeNoOp: boolean;
  rienAEcrire: string;
  propre: (faits: FaitsDe<Verbe>, etabli: DejaEtabli) => PropreAuVerbe;
}

const MOTS: { [Verbe in FaitsDeLEcritureDeForme['verbe']]: MotsDuVerbe<Verbe> } = {
  format_text: {
    ecritures: 'atomic write(s)',
    juges: 'passages',
    premierRefus: 'The write is atomic and nothing',
    expliqueLeNoOp: true,
    rienAEcrire: '(already set by hand as requested, or nothing set by hand to remove).',
    propre: (faits, { etendues, nonEcrits, sautes }) => {
      const { selection } = faits;
      return {
        // Une sélection qui ne laisse rien à juger ne fait dire conforme à aucun réglage : il n'y a pas de texte relu.
        settings: selection !== undefined && etendues.length === 0 && nonEcrits === 0 ? [] : verifierLaFormeEcrite(faits.relecture, etendues, faits.reglages, nonEcrits + sautes),
        nomsDesReglages: Object.keys(faits.reglages).join(', '),
        ...(selection === undefined ? {} : {
          erreurs: erreursDeLaSelection(selection),
          verifications: [
            { ...lireLOrigine(selection).controle, passed: selection.ecartes.length === 0 && selection.nonEvalues.length === 0 },
            ...controleDeLaListe(lireLOrigine(selection).partout),
          ],
        }),
      };
    },
  },
  format_paragraphs: {
    ecritures: 'write(s)',
    juges: 'paragraphs',
    premierRefus: 'It was the first write of the call: nothing',
    expliqueLeNoOp: false,
    rienAEcrire: '(the paragraphs already show it).',
    propre: (faits, etabli) => (faits.remiseAZero === undefined
      ? {
          settings: verifierLaFormeDeParagraphe(faits.relecture, etabli.etendues, faits.reglages, etabli.nonEcrits + etabli.sautes, etabli.laisses),
          nomsDesReglages: Object.keys(faits.reglages).join(', '),
        }
      : propreALaRemiseAZero(faits, faits.remiseAZero, etabli)),
  },
  copy_format: {
    ecritures: 'write(s)',
    juges: 'paragraphs',
    premierRefus: 'It was the first write of the call: nothing',
    expliqueLeNoOp: false,
    rienAEcrire: '(the paragraphs already show the form of the model).',
    propre: (faits, { nonEcrits, sautes, laisses }) => {
      const { copie } = faits;
      const copy = copieAuRecu(copie, faits.relecture);
      return {
        // Les cibles jugées sont celles que l'exécuteur dit entièrement écrites ; les autres sont comptées non écrites.
        settings: verifierLaCopie(faits.relecture, copie.forme, copie.cibles, nonEcrits + sautes + laisses.filter((laisse) => !laisse.ecrit).length),
        nomsDesReglages: `the form of paragraph ${copie.modele} (${Object.keys(faits.reglages).join(', ')})`,
        champs: { copy },
        erreurs: erreursDeLaCopie(copie),
        verifications: verificationsDeLaCopie(copie),
        suiteDeLEffet: ditDeLaCopie(copy),
      };
    },
  },
};

/** Le seul aiguillage sur le verbe : il apparie les faits d'un verbe à sa ligne de la table. */
function propreAuVerbe(faits: FaitsDeLEcritureDeForme, etabli: DejaEtabli): PropreAuVerbe {
  switch (faits.verbe) {
    case 'format_text': return MOTS.format_text.propre(faits, etabli);
    case 'format_paragraphs': return MOTS.format_paragraphs.propre(faits, etabli);
    case 'copy_format': return MOTS.copy_format.propre(faits, etabli);
  }
}

/** Ce qu'une remise à zéro (`reset` de `format_paragraphs`, #1867) apporte au reçu : elle se juge sur l'ORIGINE de la forme, pas sur une valeur. */
function propreALaRemiseAZero(
  faits: FaitsDe<'format_paragraphs'>,
  remiseAZero: NonNullable<FaitsDe<'format_paragraphs'>['remiseAZero']>,
  { etendues, nonEcrits, sautes, laisses }: DejaEtabli,
): PropreAuVerbe {
  const texteLaisse = remiseAZero.texteLaisse ?? [];
  const structurePerdue = remiseAZero.structureNonRedonnee;
  return {
    settings: verifierLaRemiseAZero(faits.relecture, etendues, nonEcrits + sautes, laisses, new Set(texteLaisse)),
    nomsDesReglages: 'reset',
    champs: remiseAZero.removed === undefined && remiseAZero.notRemoved === undefined && texteLaisse.length === 0 ? {} : {
      reset: {
        ...(remiseAZero.removed === undefined ? {} : { removed: remiseAZero.removed }),
        ...(remiseAZero.notRemoved === undefined ? {} : { notRemoved: remiseAZero.notRemoved }),
        ...(texteLaisse.length === 0 ? {} : { textFormattingLeft: { reason: 'pending_tracked_change' as const, ...nommer(texteLaisse) } }),
      },
    },
    erreurs: [
      // Une remise à zéro laisse un paragraphe entier pour l'une de deux raisons, chacune avec son message.
      ...(['pending_tracked_change', 'outline_level_not_restorable'] as const).flatMap((raison) => {
        const concernes = laisses.filter((laisse) => (laisse.raison ?? 'pending_tracked_change') === raison);
        if (concernes.length === 0) return [];
        return [{
          code: 'FORMAT_PARAGRAPHS_LEFT_AS_IS',
          message: `format_paragraphs did not reset ${concernes.length} targeted paragraph(s) (format.skipped, reason ${raison}): ${nommerLesParagraphes(concernes.map((laisse) => laisse.nodeId))}. `
            + (raison === 'pending_tracked_change'
              ? 'Each carries a pending tracked change that a reset would erase (on its paragraph formatting or its paragraph mark), or has only text formatting to remove while its text carries a pending tracked change (text formatting, or a move) the editor refuses to write over. '
                + 'Nothing was written on them, and resending would leave them the same way; tell the lawyer to accept or reject the pending changes of these paragraphs first.'
              : 'Each carries an outline level outside 0–9, which the editor could not give back after a reset: resetting would lose it. '
                + 'Nothing was written on them, and resending would leave them the same way; tell the lawyer these paragraphs keep their formatting.'),
        }];
      }),
      ...(texteLaisse.length === 0 ? [] : [{
        code: 'FORMAT_TEXT_FORMATTING_LEFT_AS_IS',
        message: `format_paragraphs reset the paragraph formatting of ${texteLaisse.length} paragraph(s) and left their TEXT formatting as it was (format.reset.textFormattingLeft): ${direLesParagraphes(texteLaisse)}. `
          + 'Their text carries a pending tracked change (text formatting, or a move) the editor refuses to write over; the pending change itself is untouched. '
          + 'Resending would leave their text the same way; tell the lawyer to accept or reject the pending changes of these paragraphs first, then reset them again.',
      }]),
    ],
    laisseDesReglages: texteLaisse.length > 0,
    // Ce qu'une remise à zéro retire au-delà des réglages des outils est compté : l'agent est renvoyé au compte.
    suiteDeLEffet: faits.ecritures === 0 ? '' : `${remiseAZero.removed === undefined ? '' : ' format.reset.removed counts, by category, what else the reset removed (borders and change bars included): tell the lawyer.'}`
      + `${remiseAZero.notRemoved === undefined ? '' : ' format.reset.notRemoved names formatting still in the file after the reset: it stays, and resending would not change it.'}`,
    // Une structure perdue ne se lit pas par `formatOf` et ne revient pas par un second `reset` : seule l'annulation la rend.
    suiteDeLInterruption: structurePerdue === undefined ? '' : ` Paragraph ${structurePerdue.nodeId} was reset and its page break before or outline level could NOT be given back: `
      + 'formatOf does not show them, and resending reset would not restore them. The only way back is undo_changes with '
      + `steps:${structurePerdue.ecritures}, which undoes the last ${structurePerdue.ecritures} write(s) — those made on this paragraph — and returns it to its state before the action; then tell the lawyer.`,
  };
}

/** Les identifiants, bornés à {@link CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX}, suivis du nombre de ceux qui ne sont pas nommés. */
export function nommerLesParagraphes(nodeIds: readonly string[]): string {
  const nonNommes = nodeIds.length - CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX;
  return `${nodeIds.slice(0, CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX).join(', ')}${nonNommes > 0 ? `, and ${nonNommes} more` : ''}`;
}

/** Le texte, coupé à `longueur` caractères ; un « … » marque la coupe. */
export const borne = (texte: string, longueur: number): string => (texte.length > longueur ? `${texte.slice(0, longueur)}…` : texte);

/** La cause que l'éditeur donne d'un refus, coupée à la longueur que le contrat du reçu admet pour `detail`. */
export const causeBornee = (cause: string): string => borne(cause, CANVAS_FORMAT_REFUSAL_DETAIL_MAX);

/** Le refus d'un plan par l'éditeur : son code quand il en donne un, et ce qu'il dit de lui-même, borné ({@link causeBornee}). */
export interface RefusDeLEditeur {
  code?: string;
  detail: string;
}

/** Ce que la cible désigne, une fois résolue contre le fichier de l'acte. */
export interface CibleResolue {
  /** Les paragraphes désignés qui portent du texte, dans l'ordre du document. */
  paragraphes: ReadonlyArray<{ nodeId: string }>;
  /** Le passage visé dans l'unique paragraphe, pour une cible `text`. */
  passage?: string;
  omis: NonNullable<CanvasFormatWriteReceipt['skipped']>;
  nonResolus: NonNullable<CanvasFormatWriteReceipt['unresolved']>;
  /**
   * Les cellules de tableau du corps que la cible touche, telles que sa
   * résolution les rend (`CibleDeParagraphesResolue.cellules`) : celles qui sont
   * parmi ses paragraphes, et le nombre de celles qu'une forme de paragraphe sur
   * « tout » ou une plage laisse à leur tableau. Absent : la cible est un texte.
   */
  cellules?: { rendues: readonly string[]; laissees: number };
}

/** Un passage visé — trouvé par un filtre `onlyWhere`, ou occurrence d'un texte visé dans tout l'acte : son paragraphe, et sa place dans le texte de ce paragraphe. */
export interface PassageTrouve {
  nodeId: string;
  plage: PlageDeTexte;
}

/** Ce qu'UN texte d'une liste désigne dans l'acte, une fois les occurrences de tous les textes réunies (`occurrencesDansLActe`, `planDeFormatText.ts`). */
export interface ReleveDUnTexte {
  texte: string;
  /** Ses occurrences du corps que l'écriture vise. */
  visees: number;
  /** Ses occurrences qui tiennent en entier dans celle d'un texte plus long de la liste : laissées à ce texte, qui les couvre. */
  dansUnPlusLong: number;
  /** Ses occurrences à cheval sur celle d'un texte plus long, sans y tenir : laissées telles quelles, leur part hors du plus long garde sa forme. */
  chevauchantUnPlusLong: number;
  /** Ses occurrences que portent des paragraphes hors de portée (cellule de tableau, paragraphe sans identifiant). */
  horsDePortee: number;
  /** Nombre de fois où il n'apparaît que dans un mot ou un nombre plus long. */
  accolees: number;
}

/**
 * Ce qui est visé dans tout l'acte (cible « texte » sans paragraphe, #1947),
 * tel que le reçu le redit : UN texte exact, ou une LISTE de textes avec le
 * relevé de chacun, dans l'ordre donné.
 */
export type TexteVisePartout = {
  /** Nombre d'occurrences dans le corps de l'acte, tous textes réunis : celles que l'écriture vise. */
  occurrences: number;
  /** Nombre de celles que portent des paragraphes hors de portée (`cible.omis` les nomme). */
  horsDePortee: number;
} & ({ texte: string } | { textes: readonly ReleveDUnTexte[] });

/**
 * D'où viennent les passages qu'une écriture au grain du PASSAGE vise. Trois
 * cas, et seulement trois : ce qu'un filtre `onlyWhere` trouve dans la cible
 * (#1865), les occurrences d'un texte visé dans tout l'acte (#1947), ou la
 * part de ces occurrences que le filtre retient. Ce que chaque cas change au
 * reçu est décidé une fois, par {@link lireLOrigine}.
 */
export type OrigineDeLaSelection =
  | { par: 'filtre'; filtre: CanvasTextFormatFilter }
  | { par: 'partout'; partout: TexteVisePartout }
  | { par: 'partout_et_filtre'; partout: TexteVisePartout; filtre: CanvasTextFormatFilter };

/** Les passages qu'une écriture vise, d'où ils viennent, et ce qu'il en est advenu avant l'écriture. */
export type FaitsDeLaSelection = OrigineDeLaSelection & {
  /** Les passages visés, dans l'ordre du document : ceux qui satisfont le filtre, ou toutes les occurrences sans filtre. */
  trouves: readonly PassageTrouve[];
  /**
   * Ceux des passages visés qui sont laissés tels quels — plutôt qu'écrits au
   * mauvais endroit, ou parce que l'éditeur refuse de les écrire (`detail` : sa
   * cause, quand elle seule l'explique) —, avec la raison : ni écrits, ni relus.
   */
  ecartes: ReadonlyArray<PassageTrouve & { raison: CanvasFormatSetAsideReason; detail?: string }>;
  /** Les paragraphes de la cible où le filtre ne s'évalue pas sur un passage au moins, avec leur nombre de passages. Vide sans filtre. */
  nonEvalues: ReadonlyArray<{ nodeId: string; passages: number }>;
  /** Les réglages du filtre qui ne se lisent pas sur ces passages. Vide si `nonEvalues` l'est. */
  reglagesNonLus: readonly CanvasTextFormatSettingKey[];
};

/**
 * Le verbe et ses réglages : le verbe nomme le reçu et décide de l'unité jugée
 * à la relecture — passages de texte, ou paragraphes.
 */
export type FormeDemandee =
  | {
      verbe: 'format_text';
      reglages: ReglagesDeTexte;
      /** Présent quand l'écriture vise des PASSAGES — filtre `onlyWhere`, ou texte visé dans tout l'acte : une étape du plan est alors un passage, plus un paragraphe. */
      selection?: FaitsDeLaSelection;
    }
  | {
      verbe: 'format_paragraphs';
      reglages: ReglagesDeParagraphe;
      selection?: undefined;
      /**
       * Présent pour une remise à zéro (`reset`, #1867) : `reglages` est alors
       * vide, et la relecture juge l'origine de la forme, pas une valeur.
       * `removed` et `notRemoved` sont le bilan confirmé par la relecture
       * (`bilanDeLaRemiseAZero`), rendu tel quel. `texteLaisse` nomme les
       * paragraphes remis à zéro dont la forme de TEXTE a été laissée (révision
       * en attente sur le texte). `structureNonRedonnee` : le paragraphe remis à
       * zéro juste avant l'interruption, dont le saut de page avant ou le niveau
       * de plan n'a pas pu être redonné, et le nombre d'écritures qu'il a reçues.
       */
      remiseAZero?: BilanDeRemiseAZero & { texteLaisse?: readonly string[]; structureNonRedonnee?: { nodeId: string; ecritures: number } };
    }
  | {
      verbe: 'copy_format';
      /** Les réglages copiés, par leur nom : le reçu les juge sur `copie.forme`, qui porte leurs valeurs. */
      reglages: Readonly<Record<string, true>>;
      selection?: undefined;
      /** Ce qu'une copie de forme (#1869) ajoute aux faits communs : le modèle, ce qui en est copié, ce qui est laissé. */
      copie: FaitsDeLaCopie;
    };

/** Les faits d'une écriture de forme, tels que l'exécuteur les a relevés. */
export type FaitsDeLEcritureDeForme = FormeDemandee & {
  cible: CibleResolue;
  /** L'en-tête, le pied ou les notes où l'appel écrit ; absent : le corps. Rendu par la clôture du socle. */
  lieu?: CanvasFormatPlace;
  /**
   * Ce qui est à écrire, les éléments ENTIÈREMENT écrits en tête : les
   * paragraphes de la cible — ou, pour une sélection, les passages visés
   * (`plage`) — moins ceux où l'écriture n'aurait rien changé.
   */
  aEcrire: ReadonlyArray<{ nodeId: string; plage?: PlageDeTexte }>;
  /** Nombre d'écritures à soumettre : plans atomiques (un par tranche d'étapes) et écritures par paragraphe. */
  ecrituresPrevues: number;
  /** Écritures que l'éditeur a appliquées : autant d'entrées de son historique. */
  ecritures: number;
  /** Éléments de `aEcrire` dont toutes les écritures ont été appliquées : des paragraphes, ou des passages visés. */
  etapesEcrites: number;
  /**
   * Écritures appliquées qu'aucune annulation ne défait plus, parce que l'appel
   * en compte plus que l'historique de l'éditeur n'en garde. Absent : aucune.
   */
  ecrituresNonAnnulables?: number;
  /**
   * La profondeur de l'historique de l'éditeur avant et après l'appel, quand
   * les écritures comptées n'expliquent pas l'écart (une entrée chacune) :
   * l'avocat a tapé ou annulé, ou une autre écriture est passée, entre deux.
   * Annuler par le compte du reçu déferait alors son changement. Absent :
   * l'historique a gagné ce que l'appel a écrit, ou l'éditeur ne dit pas sa
   * profondeur.
   */
  historiqueInexplique?: { avant: number; apres: number };
  /**
   * Ce qui a arrêté la série avant la première écriture non appliquée : le
   * refus de l'éditeur, ou — code `STALE_CANVAS_REVISION` — le constat que le
   * document a changé sous l'appel.
   */
  interruption?: RefusDeLEditeur;
  /**
   * Présent quand l'éditeur a refusé un lot après en avoir joué une étape —
   * dans cet appel ou dans un appel précédent sur le même document monté — et
   * qu'aucune écriture n'a relancé son peintre depuis : la page peut ne plus
   * montrer ce qui est écrit, ni ce que l'avocat tape (en-tête de
   * `ecritureDeForme.ts`, « La page après un plan refusé »).
   */
  pageNonRepeinte?: true;
  /**
   * Étapes soumises SEULES parce que l'éditeur risquait de les refuser
   * (`isolerLesEtapes`, `repliDeLot.ts`), et qu'il a écrites : chacune est sa
   * propre entrée d'historique. Absent : aucune.
   */
  ecritesSeules?: number;
  /**
   * Après une interruption : parmi les paragraphes non entièrement écrits, ceux
   * qui ont reçu une partie de leurs écritures, et ce qui est écrit, famille
   * par famille, en clair. Absent : aucun paragraphe n'est à moitié écrit.
   */
  partiels?: { nombre: number; avancement: string };
  /**
   * Paragraphes visés dont certains réglages demandés n'ont pas été écrits,
   * parce que l'écriture aurait effacé des attributs posés à la main que
   * l'éditeur ne sait pas redonner. Leurs autres réglages sont écrits et jugés
   * (`ecrit` : faux quand une interruption a empêché ces autres écritures).
   * Ils sont comptés dans `cible.omis` et ne sont ni dans `cible.paragraphes`
   * ni dans `aEcrire` : aucun compte de paragraphes écrits ne les prend.
   */
  laisses?: ReadonlyArray<ParagrapheLaisse>;
  /**
   * Paragraphes visés que l'éditeur refuse d'écrire, dans l'ordre du document,
   * avec la raison (`repliDeLot.ts`) : rien n'y est écrit, le reste de la cible
   * l'est. Ils ne sont ni dans `cible.paragraphes` ni dans `aEcrire` : aucun
   * compte de paragraphes écrits ne les prend, et ils ne sont pas relus.
   * Quand l'écriture vise des passages, ce sont eux, portés par `selection.ecartes`.
   */
  sautes?: ReadonlyArray<ParagrapheSaute>;
  /**
   * Paragraphes ÉCRITS alors qu'ils portent un changement de forme de
   * paragraphe en attente (`w:pPrChange`) : l'écriture change ce que la
   * révision propose (`ecritureDeForme.ts`, « Un paragraphe dont la forme
   * attend une décision »). Absent : aucun.
   */
  surRevisionDeParagraphe?: readonly string[];
  avant: CanvasSnapshotSummary;
  apres: CanvasSnapshotSummary;
  /** Ce que la cible visait dans le fichier lu AVANT l'écriture : ce sur quoi elle a été résolue. */
  acteAvant: ActeVise;
  relecture: RelectureDeLActe;
  /** Identités de révision apparues et disparues entre les lectures d'avant et d'après. */
  revisions: { nouvelles: string[]; disparues: string[] };
};

/** Un paragraphe visé qui n'est pas écrit — l'éditeur le refuse, ou il est épargné d'avance (`epargne`) — et pourquoi. */
export type ParagrapheSaute = { nodeId: string } & RaisonDeSaut;

/**
 * Ce qu'une écriture du socle dit de son annulation « par le compte » — UNE règle pour tous ses verbes (en-tête de
 * `ecritureDeForme.ts`, « Ce qui compte comme une écriture »).
 *
 * - `incertaine` : l'historique de l'éditeur a gagné, pendant l'appel, autre chose que ce que les écritures comptées
 *   expliquent — l'avocat a tapé ou annulé, ou une autre écriture est passée. Annuler « autant de fois que le reçu
 *   compte d'écritures » déferait alors SA frappe : aucune écriture de l'appel ne se défait sûrement par le compte.
 * - `nonAnnulables` : toutes les écritures quand l'annulation est incertaine ; sinon les premières d'un long appel,
 *   que l'historique ne garde plus.
 * - `dite` : la phrase de l'effet quand elle est incertaine, vide sinon. Elle nomme le champ du reçu qui compte.
 *
 * @param champ Le champ du reçu qui porte `nonAnnulables` (`format.writesNotUndoable`, `rewrite.writesNotUndoable`).
 */
export function annulationDeLEcriture(
  faits: { ecritures: number; ecrituresNonAnnulables?: number; historiqueInexplique?: { avant: number; apres: number } },
  champ: string,
): { incertaine: boolean; nonAnnulables: number; dite: string } {
  const { ecritures, historiqueInexplique } = faits;
  const incertaine = historiqueInexplique !== undefined && ecritures > 0;
  return {
    incertaine,
    nonAnnulables: incertaine ? ecritures : faits.ecrituresNonAnnulables ?? 0,
    dite: !incertaine ? '' : ` Undo by count is not safe: the editor's history went from ${historiqueInexplique?.avant} to ${historiqueInexplique?.apres} entries during this call for ${ecritures} write(s) `
      + '— the lawyer typed or undid, or another write ran, in between. Undoing that many steps (undo_changes) would not undo exactly these writes: '
      + `${champ} counts every write of this call. Tell the lawyer, and do not undo by count.`,
  };
}

/**
 * Ce que le reçu dit d'une page que l'éditeur ne repeint plus : ce qui est
 * écrit l'est au fichier, relu ; l'écran peut être en retard.
 */
export const PAGE_NON_REPEINTE = 'In this call or an earlier one on this document, the editor refused a batch after starting to apply it, and since then it no longer repaints its page '
  + '(a known editor defect, https://github.com/hector-ai-legal/hector-monorepo/issues/1913). What is written is in the document — '
  + 'the receipt re-read it from the exported file — but the page may not show it, nor what the lawyer types next. '
  + 'Tell the lawyer the page may be out of date while the document itself is right (the downloaded Word file shows it). Do not resend the action.';

/** Pourquoi un paragraphe épargné d'avance n'est pas écrit : ce n'est pas l'éditeur qui refuse. */
const EPARGNE_EN_CLAIR = 'spared, not refused by the editor: it carries a pending paragraph-formatting change by another author, and the editor would accept this write '
  + 'by folding it into that tracked change (rejecting the change would then undo this formatting too). It is left as is until the lawyer accepts or rejects that change';

/**
 * Pourquoi l'éditeur n'écrit pas un paragraphe ou un passage : le texte que lit
 * le modèle, en mots qu'il peut redire à l'avocat. Sous `refused_by_editor`, la
 * cause de l'éditeur suit ({@link direLeRefus}).
 */
const REFUS_EN_CLAIR: Readonly<Record<CanvasFormatEditorRefusalReason, string>> = {
  bookmark: 'a bookmark covers part of its text',
  bookmark_across_paragraphs: 'a bookmark starts in one paragraph and ends in another',
  pending_tracked_change: 'it carries a pending tracked change (a formatting change, or moved text) that the editor refuses to write over: the lawyer has to accept or reject it first',
  content_control: 'it contains a content control (a field of the document template)',
  passage_not_addressable: 'the editor cannot address a passage of text inside it (a footnote reference or moved text does this); the whole paragraph can still be targeted',
  refused_by_editor: 'the editor refuses it',
};

/** La raison d'un refus en clair, suivie de la cause de l'éditeur quand elle seule l'explique. */
const direLeRefus = (reason: CanvasFormatEditorRefusalReason, detail: string | undefined): string => (
  `${REFUS_EN_CLAIR[reason]}${detail === undefined ? '' : ` (${detail})`}`
);

/** Pourquoi un passage trouvé est laissé tel quel : le texte que lit le modèle. */
const ECART_EN_CLAIR: Readonly<Record<CanvasFormatSetAsideReason, string>> = {
  ...REFUS_EN_CLAIR,
  paragraph_text_differs: 'the editor does not count the text of the paragraph as the document file does (a footnote or endnote reference, or a non-breaking hyphen, does this), '
    + 'so a position taken from the file would not designate the same text',
  paragraph_text_unreadable: 'the editor did not return the text of the paragraph, so nothing proves where the passage is',
  position_not_proven: 'at the position of the passage, the editor targets another text',
};

/** Des paragraphes nommés jusqu'à la borne du contrat, comptés au-delà. */
export function nommer(nodeIds: readonly string[]): { nodeIds: string[]; paragraphsNotListed?: number } {
  const nonNommes = nodeIds.length - CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX;
  return { nodeIds: nodeIds.slice(0, CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX), ...(nonNommes > 0 ? { paragraphsNotListed: nonNommes } : {}) };
}

/** Les mêmes, en clair. */
function direLesParagraphes(nodeIds: readonly string[]): string {
  const { nodeIds: nommes, paragraphsNotListed } = nommer(nodeIds);
  return `${nommes.join(', ')}${paragraphsNotListed === undefined ? '' : ` and ${paragraphsNotListed} more`}`;
}

/**
 * Groupe ce qui n'est pas écrit par raison, dans l'ordre du contrat, puis par
 * cause de l'éditeur dans l'ordre où elle apparaît : chaque groupe compte ses
 * éléments et nomme leurs paragraphes.
 */
function grouperParRaison<Raison extends string>(
  elements: ReadonlyArray<{ nodeId: string; reason: Raison; detail?: string }>,
  ordre: readonly Raison[],
): Array<{ reason: Raison; count: number; nodeIds: string[]; paragraphsNotListed?: number; detail?: string }> {
  return ordre.flatMap((reason) => {
    const deCetteRaison = elements.filter((element) => element.reason === reason);
    return [...new Set(deCetteRaison.map((element) => element.detail))].map((detail) => {
      const groupe = deCetteRaison.filter((element) => element.detail === detail);
      return { reason, count: groupe.length, ...nommer(paragraphesDistincts(groupe)), ...(detail === undefined ? {} : { detail }) };
    });
  });
}

/** Un texte visé tel qu'un reçu ou un refus le recopie : entre guillemets, coupé à la longueur que le contrat admet. */
export const citer = (texte: string): string => JSON.stringify(borne(texte, CANVAS_FORMAT_QUOTED_TEXT_MAX));

/** Vrai si le texte n'a aucune occurrence en mot entier, où que ce soit : ni visée, ni laissée à un plus long, ni hors de portée. */
const estIntrouvable = (texte: ReleveDUnTexte): boolean => texte.visees + texte.dansUnPlusLong + texte.chevauchantUnPlusLong + texte.horsDePortee === 0;

/**
 * Des textes d'une liste, cités jusqu'à la borne du contrat et comptés
 * au-delà ; chacun suivi de ce qui explique qu'il ne soit pas écrit — là
 * seulement dans un mot plus long, ou hors de portée.
 */
export function direLesTextes(textes: readonly ReleveDUnTexte[]): string {
  const nonNommes = textes.length - CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX;
  const dits = textes.slice(0, CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX).map((texte) => {
    const pourquoi = [
      ...(texte.accolees === 0 ? [] : [`${texte.accolees} time(s) only inside a longer word or number, which is not an occurrence`]),
      ...(texte.horsDePortee === 0 ? [] : [`${texte.horsDePortee} occurrence(s) out of reach`]),
    ];
    return `${citer(texte.texte)}${pourquoi.length === 0 ? '' : ` (${pourquoi.join('; ')})`}`;
  });
  return `${dits.join(', ')}${nonNommes > 0 ? `, and ${nonNommes} more` : ''}`;
}

/**
 * Ce que « partout » couvre, pour un texte visé dans tout l'acte : le reçu le
 * dit même quand il est `ok`, parce qu'une occurrence dans une note, un en-tête
 * ou un pied de page n'est ni cherchée ni comptée (`occurrencesDansLActe`,
 * `planDeFormatText.ts`) et que rien d'autre ne l'apprendrait à l'agent.
 */
export function porteeDuTexteVisePartout(lieu?: CanvasFormatPlace): string {
  return lieu === undefined
    ? 'The text was searched in the body of the act and in its tables; footnotes, endnotes, headers and footers were not searched.'
    : `The text was searched in ${direLeLieu(lieu)} only.`;
}

/** Ce qu'un cas de {@link OrigineDeLaSelection} change à ce que le reçu dit et compte. */
interface LectureDeLOrigine {
  /** Les passages visés tels qu'un message les nomme, comptés : « 2 occurrence(s) of the exact text "…" ». */
  dire: (nombre: number) => string;
  /** Ce que le reçu dit quand rien n'est trouvé. */
  aucun: string;
  /** Les champs du reçu qui portent le sort de chaque passage : ceux du filtre s'il y en a un, sinon ceux des occurrences. */
  champDesSorts: 'format.onlyWhere.passages' | 'format.everywhere.occurrences';
  /** Le filtre demandé. Présent : le reçu rend `format.onlyWhere`. */
  filtre?: CanvasTextFormatFilter;
  /** Le texte visé dans tout l'acte. Présent : le reçu rend `format.everywhere`, et dit sa portée. */
  partout?: TexteVisePartout;
  /** Le contrôle que la sélection ajoute au reçu ; il passe quand aucun passage n'est écarté ni laissé non évalué. */
  controle: { name: string; message: string };
}

/** Le seul aiguillage sur l'origine d'une sélection : tout le reste du reçu lit ce qu'il rend. */
function lireLOrigine(origine: OrigineDeLaSelection): LectureDeLOrigine {
  const texteDit = (partout: TexteVisePartout): string => ('texte' in partout ? `the exact text ${citer(partout.texte)}` : `the ${partout.textes.length} exact texts given`);
  const filtreDit = (filtre: CanvasTextFormatFilter): string => `onlyWhere ${JSON.stringify(filtre)}`;
  const controleDuFiltre = {
    name: 'only_where_covered_every_passage',
    message: 'Every passage of the target must be evaluated against the onlyWhere filter, and every matching passage must be addressable in the editor.',
  };
  switch (origine.par) {
    case 'filtre':
      return {
        dire: (nombre) => `${nombre} passage(s) matching ${filtreDit(origine.filtre)}`,
        aucun: `no text in the target matches ${filtreDit(origine.filtre)}`,
        champDesSorts: 'format.onlyWhere.passages',
        filtre: origine.filtre,
        controle: controleDuFiltre,
      };
    case 'partout':
      return {
        dire: (nombre) => `${nombre} occurrence(s) of ${texteDit(origine.partout)}`,
        aucun: `the body of the act holds no occurrence of ${texteDit(origine.partout)}`,
        champDesSorts: 'format.everywhere.occurrences',
        partout: origine.partout,
        controle: { name: 'every_occurrence_addressable', message: 'Every occurrence of the targeted text must be addressable in the editor.' },
      };
    case 'partout_et_filtre':
      return {
        dire: (nombre) => `${nombre} passage(s) of ${texteDit(origine.partout)} matching ${filtreDit(origine.filtre)}`,
        aucun: `no occurrence of ${texteDit(origine.partout)} matches ${filtreDit(origine.filtre)}`,
        champDesSorts: 'format.onlyWhere.passages',
        filtre: origine.filtre,
        partout: origine.partout,
        controle: controleDuFiltre,
      };
  }
}

/** Les passages visés tels qu'un message les nomme, comptés — pour un refus que l'exécuteur rend avant toute écriture. */
export const direLesPassagesVises = (origine: OrigineDeLaSelection, nombre: number): string => lireLOrigine(origine).dire(nombre);

/** Les passages écartés, groupés par raison : le même groupe pour le reçu d'un filtre et pour celui d'un texte visé partout. */
function groupesEcartes(selection: FaitsDeLaSelection): ReturnType<typeof grouperParRaison<CanvasFormatSetAsideReason>> {
  return grouperParRaison(
    selection.ecartes.map(({ nodeId, raison, detail }) => ({ nodeId, reason: raison, ...(detail === undefined ? {} : { detail }) })),
    CanvasFormatSetAsideReasonSchema.options,
  );
}

/** Les passages qu'un filtre n'a pas pu évaluer, tels que le reçu les compte. `undefined` : aucun. */
function nonEvaluesAuRecu(selection: FaitsDeLaSelection): NonNullable<CanvasFormatWriteReceipt['onlyWhere']>['passagesNotEvaluated'] {
  const [premierReglage, ...autresReglages] = selection.reglagesNonLus;
  const nonEvalues = selection.nonEvalues.reduce((somme, paragraphe) => somme + paragraphe.passages, 0);
  if (nonEvalues === 0 || premierReglage === undefined) return undefined;
  return { count: nonEvalues, settings: [premierReglage, ...autresReglages], ...nommer(selection.nonEvalues.map((paragraphe) => paragraphe.nodeId)) };
}

/**
 * Ce que le reçu dit des occurrences d'un texte visé dans tout l'acte.
 *
 * @param sorts Le sort de chaque occurrence, quand c'est elle qui est écrite ;
 *   `undefined` sous un filtre : les sorts sont alors ceux des passages retenus
 *   (`format.onlyWhere`).
 */
function occurrencesAuRecu(
  partout: TexteVisePartout,
  sorts: { ecrits: ReadonlyArray<{ plage?: PlageDeTexte }>; dejaPosees: number; nonEcrites: number; ecartees: ReturnType<typeof groupesEcartes> } | undefined,
): NonNullable<CanvasFormatWriteReceipt['everywhere']> {
  const [premier, ...autres] = sorts?.ecartees ?? [];
  return {
    occurrencesFound: partout.occurrences,
    ...(sorts === undefined ? {} : {
      occurrencesWritten: sorts.ecrits.length,
      ...(sorts.dejaPosees === 0 ? {} : { occurrencesAlreadyFormatted: sorts.dejaPosees }),
      ...(sorts.nonEcrites === 0 ? {} : { occurrencesNotWritten: sorts.nonEcrites }),
      ...(premier === undefined ? {} : { occurrencesSetAside: [premier, ...autres] }),
    }),
    ...(partout.horsDePortee === 0 ? {} : { occurrencesOutOfReach: partout.horsDePortee }),
    ...('textes' in partout ? { texts: textesAuRecu(partout.textes, sorts?.ecrits) } : {}),
  };
}

/**
 * Le compte de chaque texte d'une liste, tel que le reçu le rend : les textes à
 * occurrences d'un côté, les introuvables de l'autre, chaque liste bornée.
 *
 * @param ecrits Les occurrences écrites — celle d'un texte porte ce texte ;
 *   `undefined` sous un filtre, où ce qui s'écrit n'est plus l'occurrence.
 */
function textesAuRecu(
  textes: readonly ReleveDUnTexte[],
  ecrits: ReadonlyArray<{ plage?: PlageDeTexte }> | undefined,
): NonNullable<NonNullable<CanvasFormatWriteReceipt['everywhere']>['texts']> {
  const introuvables = textes.filter(estIntrouvable);
  const aOccurrences = textes.filter((texte) => !estIntrouvable(texte));
  const auDela = (liste: readonly unknown[]): number => liste.length - CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX;
  return {
    given: textes.length,
    ...(aOccurrences.length === 0 ? {} : {
      perText: aOccurrences.slice(0, CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX).map((texte) => ({
        text: borne(texte.texte, CANVAS_FORMAT_QUOTED_TEXT_MAX),
        found: texte.visees,
        ...(ecrits === undefined ? {} : { written: ecrits.filter(({ plage }) => plage?.texte === texte.texte).length }),
        ...(texte.dansUnPlusLong === 0 ? {} : { insideLongerText: texte.dansUnPlusLong }),
        ...(texte.chevauchantUnPlusLong === 0 ? {} : { overlappingLongerText: texte.chevauchantUnPlusLong }),
        ...(texte.horsDePortee === 0 ? {} : { outOfReach: texte.horsDePortee }),
      })),
    }),
    ...(auDela(aOccurrences) > 0 ? { textsNotListed: auDela(aOccurrences) } : {}),
    ...(introuvables.length === 0 ? {} : {
      notFound: introuvables.slice(0, CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX).map((texte) => ({
        text: borne(texte.texte, CANVAS_FORMAT_QUOTED_TEXT_MAX),
        ...(texte.accolees === 0 ? {} : { insideLongerWord: texte.accolees }),
      })),
    }),
    ...(auDela(introuvables) > 0 ? { notFoundNotListed: auDela(introuvables) } : {}),
  };
}

/** Les erreurs qu'une écriture au grain du passage ajoute au reçu : ce qui est visé sans être écrit, ce que le filtre n'a pas pu évaluer. */
function erreursDeLaSelection(selection: FaitsDeLaSelection): CanvasActionReceipt['errors'] {
  const lecture = lireLOrigine(selection);
  const ecartes = groupesEcartes(selection);
  const nonEvalues = nonEvaluesAuRecu(selection);
  return [
    ...(ecartes.length === 0 ? [] : [{
      code: 'FORMAT_PASSAGES_SET_ASIDE',
      message: `format_text left ${lecture.dire(selection.ecartes.length)} as they were, rather than write beside them: `
        + ecartes.map((groupe) => (
          `${groupe.count} in paragraph(s) ${direLesParagraphes(paragraphesDistincts(selection.ecartes.filter((passage) => passage.raison === groupe.reason && passage.detail === groupe.detail)))} — `
          + `${ECART_EN_CLAIR[groupe.reason]}${groupe.detail === undefined ? '' : ` (${groupe.detail})`}`
        )).join('; ')
        + '. Do not resend the action: it would set them aside again. Tell the lawyer which paragraphs still need this formatting by hand.',
    }]),
    ...erreursDeLaListe(lecture.partout),
    ...(nonEvalues === undefined || lecture.filtre === undefined ? [] : [{
      code: 'FORMAT_FILTER_NOT_EVALUATED',
      message: `format_text could not evaluate onlyWhere ${JSON.stringify(lecture.filtre)} on ${nonEvalues.count} passage(s), in paragraph(s) ${direLesParagraphes(selection.nonEvalues.map((paragraphe) => paragraphe.nodeId))}: `
        + `${nonEvalues.settings.join(', ')} cannot be read there (a style missing from the file, a theme colour, a superscript size). `
        + 'They were neither matched nor written, and they may or may not have the form the filter asks for. '
        + 'Do not resend the action; tell the lawyer which paragraphs could not be checked. A form read (formatOf) of them shows the settings under "unreadable".',
    }]),
  ];
}

/**
 * Ce qu'une LISTE de textes ajoute aux erreurs du reçu : les textes sans aucune
 * occurrence — les autres sont écrits quand même —, et les occurrences à cheval
 * sur celle d'un texte plus long. Rien pour un texte seul : sans occurrence,
 * l'appel est refusé avant toute écriture.
 */
function erreursDeLaListe(partout: TexteVisePartout | undefined): CanvasActionReceipt['errors'] {
  if (partout === undefined || !('textes' in partout)) return [];
  const introuvables = partout.textes.filter(estIntrouvable);
  const aCheval = partout.textes.filter((texte) => texte.chevauchantUnPlusLong > 0);
  return [
    ...(introuvables.length === 0 ? [] : [{
      code: 'FORMAT_TEXTS_NOT_FOUND',
      message: `format_text found no whole-word occurrence of ${introuvables.length} of the ${partout.textes.length} texts given (format.everywhere.texts.notFound): `
        + `${direLesTextes(introuvables)}. Every other text was handled. `
        + 'A text not found may be written differently in the act (case, spaces, punctuation) or not be there. Do not resend the whole action: '
        + 'tell the lawyer which texts were not found, or read the act and send only those, copied exactly as it reads.',
    }]),
    ...(aCheval.length === 0 ? [] : [{
      code: 'FORMAT_TEXTS_OVERLAP',
      message: `format_text left ${aCheval.reduce((somme, texte) => somme + texte.chevauchantUnPlusLong, 0)} occurrence(s) as they were (format.everywhere.texts.perText, overlappingLongerText): `
        + `${aCheval.slice(0, CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX).map((texte) => `${citer(texte.texte)} (${texte.chevauchantUnPlusLong})`).join(', ')}`
        + `${aCheval.length > CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX ? `, and ${aCheval.length - CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX} more` : ''}. `
        + 'Each overlaps an occurrence of a longer text of the list without lying inside it, and two writes cannot overlap: the longer text was written, '
        + 'and the part of these occurrences outside it keeps its form. To format it, send that text alone in another call.',
    }]),
  ];
}

/** Le contrôle qu'une liste de textes ajoute au reçu : il passe quand chaque texte a ses occurrences, et qu'aucune n'est laissée à cheval. */
function controleDeLaListe(partout: TexteVisePartout | undefined): VerificationCheck[] {
  if (partout === undefined || !('textes' in partout)) return [];
  return [{
    name: 'every_listed_text_handled',
    passed: erreursDeLaListe(partout).length === 0,
    message: 'Every text of the list must have at least one whole-word occurrence, and none of its occurrences may be left overlapping a longer text of the list.',
  }];
}

/**
 * Où l'avocat met en forme ce que les verbes de forme n'écrivent pas. Mesuré sur
 * l'éditeur réel (`tests/ws-mocked/mesure-barre-d-outils-forme.spec.ts`,
 * SuperDoc 2.15.1, 2026-10-05) : le gras et le barré posés par la barre du
 * canvas sur un mot sélectionné dans une cellule de tableau DU CORPS sont au
 * fichier. Rien n'est mesuré dans un en-tête, un pied ou une note : cette issue
 * n'y est pas promise ({@link direLesHorsDePortee}).
 */
const ISSUE_PAR_LA_BARRE = 'the lawyer can format it in the canvas with the toolbar, on the selected text';

/**
 * Pourquoi un paragraphe de tableau d'un en-tête, d'un pied ou d'une note reste tel quel : dit une fois, pour une cible
 * par identifiants comme pour une plage. Dans le corps, une cellule s'écrit et se relit comme un autre paragraphe (§16
 * de `superdoc-document-api.md`).
 */
const TABLEAU_D_UN_LIEU_NON_ECRIT = 'the formatting actions do not write the text of a table inside a header, a footer or a footnote';

const RAISON_EN_CLAIR: Readonly<Record<CanvasFormatUnresolvedReason, string>> = {
  not_found: 'no paragraph of the document body, table cells included, carries this id',
  table_cell: `it is a table-cell paragraph — ${TABLEAU_D_UN_LIEU_NON_ECRIT}`,
  duplicate_node_id: 'the document file gives this id to two paragraphs, so the formatting actions cannot tell which one to write',
};

/** Ce qu'une plage ou « tout l'acte » couvre et que les verbes de forme n'atteignent pas : compté, jamais nommé. */
const HORS_DE_PORTEE_EN_CLAIR: Readonly<Record<CanvasFormatSkippedReason, string>> = {
  table_cell: `in table cells — ${TABLEAU_D_UN_LIEU_NON_ECRIT}`,
  no_node_id: 'without an identifier in the file, which the formatting actions cannot address',
  duplicate_node_id: 'whose identifier another paragraph also carries, so the formatting actions cannot tell them apart',
};

/**
 * Ce que le reçu dit des paragraphes que la cible COUVRE et que le verbe
 * n'atteint pas (cellules de tableau d'un en-tête, d'un pied ou d'une note,
 * paragraphes sans identifiant). Absent : aucun.
 *
 * WHY une erreur et pas un simple compte : sans elle, une cible « tout » rend un
 * reçu `ok` et l'agent annonce une mise en forme entière (#1870). Le
 * modèle d'une copie pris dans sa propre cible (`copy_source`) n'en est pas :
 * il montre déjà la forme demandée. Un paragraphe sans texte (`no_text`) non
 * plus : il n'a pas de texte à mettre en forme.
 */
export function direLesHorsDePortee(
  verbe: FaitsDeLEcritureDeForme['verbe'],
  omis: NonNullable<CanvasFormatWriteReceipt['skipped']>,
  lieu: CanvasFormatPlace | undefined,
): string | undefined {
  const groupes = omis.flatMap(({ reason, count, nodeIds, paragraphsNotListed }) => (
    reason === 'table_cell' || reason === 'no_node_id' || reason === 'duplicate_node_id' ? [{ reason, count, nodeIds, paragraphsNotListed } as const] : []
  ));
  if (groupes.length === 0) return undefined;
  const nombre = groupes.reduce((somme, groupe) => somme + groupe.count, 0);
  // Une plage ou « tout l'acte » ne nomme pas ce qu'elle couvre ; un texte visé partout nomme les cellules qui le portent.
  const nommes = ({ nodeIds, paragraphsNotListed }: (typeof groupes)[number]): string => (
    nodeIds === undefined ? '' : ` (${nodeIds.join(', ')}${paragraphsNotListed === undefined ? '' : ` and ${paragraphsNotListed} more`})`
  );
  return `${verbe} did NOT format ${nombre} paragraph(s) its target covers (format.skipped): `
    + `${groupes.map((groupe) => `${groupe.count} ${HORS_DE_PORTEE_EN_CLAIR[groupe.reason]}${nommes(groupe)}`).join('; ')}. `
    + `Tell the lawyer this text keeps its form${lieu === undefined ? `, and that ${ISSUE_PAR_LA_BARRE}` : ''}. Do not resend the action: they would be left out again.`;
}

/** Les identifiants non résolus, chacun avec sa raison en clair : le texte que lit le modèle. */
export function decrireLesNonResolus(nonResolus: CibleResolue['nonResolus']): string {
  return nonResolus.map(({ nodeId, reason }) => `${nodeId} (${RAISON_EN_CLAIR[reason]})`).join(', ');
}

/** Ce qui distingue une étendue d'une autre dans un même acte : son paragraphe, et sa place quand c'est un passage. */
const cleDEtendue = ({ nodeId, plage }: { nodeId: string; plage?: PlageDeTexte }): string => `${nodeId}:${plage?.debut ?? ''}`;

/** Les paragraphes distincts d'une suite d'étendues, dans leur ordre d'apparition. */
const paragraphesDistincts = (etendues: ReadonlyArray<{ nodeId: string }>): string[] => [...new Set(etendues.map((etendue) => etendue.nodeId))];

/** Vrai si le fichier relu porte, paragraphe par paragraphe, exactement la forme du fichier d'avant. */
function memeForme(relecture: RelectureDeLActe, acteAvant: ActeVise): boolean {
  return 'acte' in relecture && JSON.stringify(relecture.acte.paragraphes) === JSON.stringify(acteAvant.paragraphes);
}

/** Les paragraphes sautés, groupe par groupe, en clair : leur nombre, la raison — refus de l'éditeur, ou épargne —, leurs identifiants. */
function direLesSautes(sautes: ReadonlyArray<ParagrapheSaute>): string {
  const epargnes = sautes.filter((saute) => saute.epargne === true);
  const refuses = sautes.filter((saute) => saute.epargne !== true);
  return [
    ...grouperParRaison(refuses, CanvasFormatEditorRefusalReasonSchema.options).map((groupe) => (
      `${groupe.count} — ${direLeRefus(groupe.reason, groupe.detail)}: `
      + direLesParagraphes(refuses.filter((saute) => saute.reason === groupe.reason && saute.detail === groupe.detail).map((saute) => saute.nodeId))
    )),
    ...(epargnes.length === 0 ? [] : [`${epargnes.length} — ${EPARGNE_EN_CLAIR}: ${direLesParagraphes(epargnes.map((saute) => saute.nodeId))}`]),
  ].join('; ');
}

/** Ce qui a fait laisser des paragraphes : l'éditeur, une épargne, ou les deux. */
function quiLaisse(sautes: ReadonlyArray<ParagrapheSaute>): string {
  const epargnes = sautes.filter((saute) => saute.epargne === true).length;
  if (epargnes === 0) return 'because the editor refuses to format them';
  return epargnes === sautes.length ? 'because they are spared (a pending formatting change would be rewritten)' : 'because the editor refuses to format them, or because they are spared';
}

/**
 * Décide le reçu d'une écriture de forme.
 *
 * Quatre issues :
 *  - `aborted` : la PREMIÈRE écriture est refusée, ou l'éditeur refuse TOUT ce
 *    que la cible désigne, et rien n'est écrit — prouvé par une révision
 *    inchangée ou par un fichier identique (la révision avance sur un refus
 *    sans que rien soit écrit) ;
 *  - `non_verifiable` : la même situation, sans rien qui prouve que le
 *    document est intact ;
 *  - `ok` : chaque réglage relu conforme, cible entièrement résolue, aucune
 *    révision créée — y compris quand il n'y avait rien à écrire, ou qu'un
 *    filtre `onlyWhere` n'a trouvé aucun passage ;
 *  - `partial` : le reçu nomme ce qui diffère, ce qui n'a pas été jugé, ce qui
 *    n'a pas été écrit. Jamais à rejouer.
 *
 * Quand l'écriture vise des passages — filtre `onlyWhere`, texte visé dans tout
 * l'acte —, tout se compte et se juge au grain du PASSAGE visé
 * (`format.onlyWhere`, `format.everywhere`) : ce qui n'est pas visé n'est ni
 * écrit ni relu.
 */
export function recuDeLEcritureDeForme(faits: FaitsDeLEcritureDeForme): CanvasActionReceipt {
  const { interruption, cible, aEcrire, ecritures, etapesEcrites, selection } = faits;
  // Ce que l'origine de la sélection change au reçu : lu une fois, ici.
  const lecture = selection === undefined ? undefined : lireLOrigine(selection);
  const INTENTION = faits.verbe;
  const mots = MOTS[faits.verbe];
  const sautes = faits.sautes ?? [];
  // Rien de ce que la cible désigne n'est écrit ni déjà en forme : tout paragraphe visé est sauté, ou — sous un
  // filtre — l'éditeur refuse chaque passage trouvé. Un passage écarté pour une autre raison (sa position n'est pas
  // prouvée) n'est pas un refus : il reste dit par le reçu des passages.
  const toutEstRefuse = interruption === undefined && (selection === undefined
    ? sautes.length > 0 && cible.paragraphes.length === 0
    : selection.trouves.length > 0 && selection.ecartes.length === selection.trouves.length && selection.ecartes.every((passage) => estUnRefusDeLEditeur(passage.raison)));

  if ((interruption !== undefined || toutEstRefuse) && ecritures === 0) {
    const cause = interruption?.detail ?? (selection === undefined || lecture === undefined
      ? `none of the ${sautes.length} targeted paragraph(s) can be formatted: ${direLesSautes(sautes)}`
      : `none of the ${lecture.dire(selection.trouves.length)} can be formatted: `
        + groupesEcartes(selection)
          .map((groupe) => `${groupe.count} in paragraph(s) ${direLesParagraphes(groupe.nodeIds)} — ${ECART_EN_CLAIR[groupe.reason]}${groupe.detail === undefined ? '' : ` (${groupe.detail})`}`).join('; '));
    // Ce qu'une remise à zéro a laissé d'avance reste vrai quand tout le reste est sauté : nommé, avec sa raison.
    const laissesDAvance = (faits.laisses ?? []).flatMap((laisse) => (laisse.raison === undefined ? [] : [{ nodeId: laisse.nodeId, reason: laisse.raison }]));
    const laissesDits = laissesDAvance.length === 0 ? '' : ` Besides, ${laissesDAvance.length} more targeted paragraph(s) were left untouched before any write: `
      + `${grouperParRaison(laissesDAvance, ['pending_tracked_change', 'outline_level_not_restorable']).map((groupe) => `${groupe.count} (${groupe.reason}): ${direLesParagraphes(groupe.nodeIds)}`).join('; ')}. `
      + 'pending_tracked_change: a pending tracked change the action would erase or cannot write over — the lawyer has to accept or reject it first; '
      + 'outline_level_not_restorable: an outline level the editor could not give back.';
    // La page que l'éditeur ne repeint plus se dit aussi quand rien n'est écrit : ce que l'avocat tape n'y paraît plus.
    const pageDite = faits.pageNonRepeinte === true ? ` Besides — ${PAGE_NON_REPEINTE}` : '';
    // Ce que la cible couvrait hors de portée du verbe reste vrai quand rien n'est écrit.
    const horsDePorteeDit = direLesHorsDePortee(INTENTION, cible.omis, faits.lieu);
    const horsDePorteeDeLaCible = horsDePorteeDit === undefined ? '' : ` Besides — ${horsDePorteeDit}`;
    const rienDEcrit = faits.apres.revision === faits.avant.revision || memeForme(faits.relecture, faits.acteAvant);
    if (!rienDEcrit) {
      return inconclusiveAfterMutation({
        intent: INTENTION,
        snapshot: faits.avant,
        postSnapshot: faits.apres,
        code: 'FORMAT_WRITE_UNCONFIRMED',
        message: `${INTENTION} was submitted and the editor refused it (${cause}), yet the document changed during the action `
          + 'and its file could not prove that nothing was written. The formatting may or may not be applied; do not resend it. '
          + 'Tell the lawyer it may or may not be in place, then read the form of the target (canvas_inspect with formatOf) to say which.',
      });
    }
    return {
      ...abortedBeforeMutation({
        intent: INTENTION,
        snapshot: faits.avant,
        code: 'FORMAT_WRITE_REFUSED',
        message: interruption === undefined
          ? `${INTENTION} left its whole target as it was: ${cause}. Nothing was written: the form of the document is the same before and after. `
            + `Resending the same action will end the same way; tell the lawyer why the target keeps its form.${laissesDits}${horsDePorteeDeLaCible}${pageDite}`
          : `${interruption.code === 'STALE_CANVAS_REVISION' ? `${INTENTION} stopped before its first write` : `${INTENTION} was refused by the editor`} (${interruption.detail}). ${mots.premierRefus} was written: `
          + 'the form of the document is the same before and after. '
          // Le document a changé sous l'appel : relire puis renvoyer aboutit, contrairement à un refus de l'éditeur.
          + (interruption.code === 'STALE_CANVAS_REVISION'
            ? 'Read the target again (formatOf) and resend the action.'
            : interruption.code === 'NO_OP' && mots.expliqueLeNoOp
              ? `NO_OP means the editor found at least one targeted ${selection === undefined ? 'paragraph' : 'passage'} where the requested settings change nothing, `
                + `and it then refuses the whole write. You may retry: read the form of the target (formatOf) and resend on the ${selection === undefined ? 'paragraphs' : 'passages'} that still need it; `
                + 'if none does, tell the lawyer the text already shows this form.'
              : 'Tell the lawyer the formatting was not applied, and why. Resending the same action will be refused the same way: read the form of the target (formatOf) before trying another.')
          // Ce qui était visé sans pouvoir être écrit reste vrai : le refus ne l'efface pas.
          + (selection === undefined ? '' : erreursDeLaSelection(selection).map((erreur) => ` Besides — ${erreur.message}`).join(''))
          + horsDePorteeDeLaCible
          + pageDite,
      }),
      // La révision a pu avancer sans écriture : le reçu le dit, la portée d'avant ne vaut plus.
      postSnapshot: faits.apres,
    };
  }

  const nonEcrits = aEcrire.slice(etapesEcrites);
  const ecrits = aEcrire.slice(0, etapesEcrites);
  // Une étape est un paragraphe ; quand l'écriture vise des passages, c'est un passage visé.
  const horsJugement = new Set([...nonEcrits, ...(selection?.ecartes ?? [])].map(cleDEtendue));
  // Jugés : tout ce que la cible désigne — les passages visés, s'il y en a — sauf ce qu'une écriture non appliquée
  // portait (compté non écrit) et ce qui a été écarté (compté écarté).
  const etendues: EtendueEcrite[] = selection === undefined
    ? cible.paragraphes.filter((paragraphe) => !horsJugement.has(cleDEtendue(paragraphe))).map((paragraphe) => ({
        nodeId: paragraphe.nodeId,
        ...(cible.passage === undefined ? {} : { passage: cible.passage }),
      }))
    : selection.trouves.filter((passage) => !horsJugement.has(cleDEtendue(passage)));
  const laisses = faits.laisses ?? [];
  // Un paragraphe que l'éditeur refuse d'écrire est un paragraphe visé non écrit : aucun réglage n'est conforme pour la demande entière.
  const propre = propreAuVerbe(faits, { etendues, nonEcrits: nonEcrits.length, sautes: sautes.length, laisses });
  const { settings } = propre;
  // Ce qu'une remise à zéro laisse d'avance est compté et nommé par raison, comme ce que l'éditeur refuse.
  const laissesParLaRemise = laisses.flatMap((laisse) => (laisse.raison === undefined ? [] : [{ nodeId: laisse.nodeId, reason: laisse.raison }]));
  // Les cellules qu'une forme de paragraphe sur « tout » ou une plage laisse à leur tableau : comptées, sans dégrader le reçu.
  const cellulesLaissees = cible.cellules?.laissees ?? 0;
  const omis: NonNullable<CanvasFormatWriteReceipt['skipped']> = [
    ...cible.omis,
    ...(cellulesLaissees === 0 ? [] : [{ reason: 'table_cell_by_ids_only' as const, count: cellulesLaissees }]),
    ...grouperParRaison(laissesParLaRemise, ['pending_tracked_change', 'outline_level_not_restorable']),
    ...grouperParRaison(sautes, CanvasFormatEditorRefusalReasonSchema.options),
  ];
  const surRevision = faits.surRevisionDeParagraphe ?? [];
  const ecritesSeules = faits.ecritesSeules ?? 0;
  const { partiels } = faits;
  // Les paragraphes dont une écriture est écartée parce qu'elle effacerait des attributs posés à la main.
  const laissesPourLeursAttributs = laisses.filter((laisse) => laisse.raison === undefined);
  const { incertaine: annulationIncertaine, nonAnnulables, dite: annulationDite } = annulationDeLEcriture(faits, 'format.writesNotUndoable');
  const dejaPoses = (selection === undefined ? cible.paragraphes.length : selection.trouves.length - selection.ecartes.length) - aEcrire.length;
  const [premierEcarte, ...autresEcartes] = selection === undefined ? [] : groupesEcartes(selection);
  const nonEvalues = selection === undefined ? undefined : nonEvaluesAuRecu(selection);
  // Une forme de TEXTE vaut aussi dans les tableaux : le reçu dit combien des paragraphes écrits sont des cellules.
  const enCellule = new Set(faits.verbe === 'format_text' ? cible.cellules?.rendues : []);
  const cellulesEcrites = paragraphesDistincts(ecrits).filter((nodeId) => enCellule.has(nodeId)).length;
  const format: CanvasFormatWriteReceipt = {
    ...(faits.lieu === undefined ? {} : { in: faits.lieu }),
    paragraphsWritten: selection === undefined ? etapesEcrites : paragraphesDistincts(ecrits).length,
    ...(cellulesEcrites === 0 ? {} : { tableCellParagraphsWritten: cellulesEcrites }),
    writes: ecritures,
    ...(nonAnnulables === 0 ? {} : { writesNotUndoable: nonAnnulables }),
    ...(dejaPoses === 0 || selection !== undefined ? {} : { paragraphsAlreadyFormatted: dejaPoses }),
    ...(nonEcrits.length === 0 ? {} : { paragraphsNotWritten: selection === undefined ? nonEcrits.length : paragraphesDistincts(nonEcrits).length }),
    ...(partiels === undefined || partiels.nombre === 0 ? {} : { paragraphsPartlyWritten: partiels.nombre }),
    settings,
    ...propre.champs,
    // Sous un filtre, le sort de chaque passage est dans `onlyWhere` ; sans filtre, celui de chaque occurrence dans `everywhere`.
    ...(selection === undefined || lecture?.filtre === undefined ? {} : {
      onlyWhere: {
        passagesFound: selection.trouves.length,
        passagesWritten: etapesEcrites,
        ...(dejaPoses === 0 ? {} : { passagesAlreadyFormatted: dejaPoses }),
        ...(nonEcrits.length === 0 ? {} : { passagesNotWritten: nonEcrits.length }),
        ...(premierEcarte === undefined ? {} : { passagesSetAside: [premierEcarte, ...autresEcartes] }),
        ...(nonEvalues === undefined ? {} : { passagesNotEvaluated: nonEvalues }),
      },
    }),
    ...(lecture?.partout === undefined ? {} : {
      everywhere: occurrencesAuRecu(lecture.partout, lecture.filtre !== undefined ? undefined : {
        ecrits,
        dejaPosees: dejaPoses,
        nonEcrites: nonEcrits.length,
        ecartees: premierEcarte === undefined ? [] : [premierEcarte, ...autresEcartes],
      }),
    }),
    // Le contrat refuse une liste vide : un champ absent dit « rien d'omis ».
    ...(omis.length === 0 ? {} : { skipped: omis }),
    ...(cible.nonResolus.length === 0 ? {} : { unresolved: cible.nonResolus }),
    ...(faits.pageNonRepeinte === true ? { pageNotRepainted: true as const } : {}),
    ...(surRevision.length === 0 ? {} : { writtenOverPendingParagraphRevision: nommer(surRevision) }),
  };
  const unite = selection === undefined ? 'paragraphs' : 'passages';

  const { nouvelles, disparues } = faits.revisions;
  const differents = settings.filter((reglage) => reglage.outcome === 'different').map((reglage) => reglage.setting);
  // Un réglage non jugé pour la seule raison que des paragraphes ne sont pas écrits est dit par l'interruption.
  const nonRelus = settings
    // … et un réglage laissé tel quel, par l'erreur qui nomme ces paragraphes.
    .filter((reglage) => reglage.outcome === 'unreadable' && !['not_written', 'attributes_would_be_lost', 'pending_tracked_change', 'outline_level_not_restorable', 'list_numbering'].includes(reglage.unreadable?.reason ?? ''))
    .map((reglage) => reglage.setting);

  const horsDePortee = direLesHorsDePortee(INTENTION, cible.omis, faits.lieu);
  const erreurs: CanvasActionReceipt['errors'] = [
    ...(interruption === undefined ? [] : [{
      code: 'FORMAT_WRITE_INTERRUPTED',
      message: `${INTENTION} wrote ${etapesEcrites} of ${aEcrire.length} ${unite} in ${ecritures} ${mots.ecritures}, `
        + `then ${interruption.code === 'STALE_CANVAS_REVISION' ? 'stopped before the next one' : 'the editor refused the next one'} (${interruption.detail}). `
        + (lecture !== undefined
          ? `${lecture.dire(nonEcrits.length)} were NOT written (${lecture.champDesSorts}NotWritten), so no setting is confirmed for the whole request. `
          : partiels === undefined || partiels.nombre === 0
            ? `${nonEcrits.length} targeted paragraph(s) were NOT written (format.paragraphsNotWritten), so no setting is confirmed for the whole request. `
            : `${nonEcrits.length} targeted paragraph(s) were not entirely written (format.paragraphsNotWritten), ${partiels.nombre} of them partly `
              + `(format.paragraphsPartlyWritten) — ${partiels.avancement} — so no setting is confirmed for the whole request. `)
        + (faits.lieu === undefined
          ? `What is written stays written; do not resend the whole action. Tell the lawyer the formatting stopped part-way. The ${unite} not written start at `
            + `${nonEcrits[0]?.nodeId ?? 'the paragraph after the last written one'}: to finish, read their form (formatOf) and send the action on them only.`
          // Dans un lieu, une cible ne nomme pas ses paragraphes : la suite se fait en relisant le lieu, puis sur le lieu entier.
          : `What is written stays written. Tell the lawyer the formatting stopped part-way in ${direLeLieu(faits.lieu)}. The ${unite} not written start at `
            + `${nonEcrits[0]?.nodeId ?? 'the paragraph after the last written one'}: to finish, read ${direLeLieu(faits.lieu)} again (canvas_inspect with formatOf and the same "in"), `
            + 'then send the action again on that place — what already shows the requested form is not written twice.')
        + (propre.suiteDeLInterruption ?? ''),
    }]),
    ...(differents.length === 0 ? [] : [{
      code: 'FORMAT_NOT_AS_REQUESTED',
      message: `${INTENTION} was written, but the document re-read after the write does not show what was requested for: ${differents.join(', ')}. `
        + `format.settings gives, for each, the value actually read and the ${mots.juges} concerned. Do not resend the same action; `
        + 'tell the lawyer exactly what differs.',
    }]),
    ...(nonRelus.length === 0 ? [] : [{
      code: 'FORMAT_NOT_VERIFIED',
      message: `${INTENTION} was written, but these settings could not be re-read from the document: ${nonRelus.join(', ')} `
        + '(format.settings gives the reason). They are neither confirmed nor refuted: do not resend the action. '
        + 'Tell the lawyer these settings were written but could not be checked; a form read (formatOf) of the target shows what the document carries now.',
    }]),
    ...(cible.nonResolus.length === 0 ? [] : [{
      code: 'FORMAT_TARGET_PARTLY_UNRESOLVED',
      message: `${INTENTION} handled the paragraphs it found, and left out ids it could not resolve: ${decrireLesNonResolus(cible.nonResolus)}. `
        + 'Do not resend the whole action; tell the lawyer what was not formatted.',
    }]),
    ...(laissesPourLeursAttributs.length === 0 ? [] : [{
      code: 'FORMAT_PARAGRAPHS_LEFT_AS_IS',
      message: `${INTENTION} did not write ${[...new Set(laissesPourLeursAttributs.flatMap((laisse) => laisse.reglages))].join(', ')} on ${laissesPourLeursAttributs.length} targeted paragraph(s) `
        + `(format.skipped, reason attributes_would_be_lost): ${nommerLesParagraphes(laissesPourLeursAttributs.map((laisse) => laisse.nodeId))}. `
        + `They carry attributes set by hand that the editor cannot keep when it writes that part of the paragraph (${[...new Set(laissesPourLeursAttributs.flatMap((laisse) => laisse.attributs))].join(', ')}): `
        + 'writing would have changed a setting nobody asked for. Their other requested settings were handled as on every other paragraph '
        + '(format.settings says, setting by setting, what is conforming). Resending these settings on these paragraphs would leave them the same way; '
        + 'tell the lawyer which paragraphs keep their form for these settings.',
    }]),
    ...(sautes.length === 0 ? [] : [{
      code: 'FORMAT_PARAGRAPHS_SKIPPED',
      message: `${INTENTION} left ${sautes.length} targeted paragraph(s) as they were, ${quiLaisse(sautes)} (format.skipped names them): `
        + `${direLesSautes(sautes)}. Every other targeted paragraph was handled; format.settings counts the skipped ones as not written, `
        + 'so no setting is confirmed for the whole request. Do not resend the action: these paragraphs would be left the same way. '
        + 'Tell the lawyer which paragraphs keep their form, and why.',
    }]),
    ...(surRevision.length === 0 ? [] : [{
      code: 'FORMAT_WRITTEN_OVER_PENDING_REVISION',
      message: `${INTENTION} wrote on ${surRevision.length} targeted paragraph(s) that carry a pending tracked change of paragraph formatting (format.writtenOverPendingParagraphRevision): ${direLesParagraphes(surRevision)}. `
        + 'The pending change is still there to accept or reject, with its author and date, but the form just written is now part of what it proposes: '
        + 'if the lawyer accepts it the written form stays, and if the lawyer rejects it the written form is lost with it. '
        + 'Do not resend; tell the lawyer, so these pending changes are decided knowingly.',
    }]),
    ...(propre.erreurs ?? []),
    ...(horsDePortee === undefined ? [] : [{ code: 'FORMAT_PARAGRAPHS_OUT_OF_REACH', message: horsDePortee }]),
    ...(nouvelles.length === 0 ? [] : [{
      code: 'FORMAT_CREATED_TRACKED_CHANGES',
      message: `${INTENTION} is a direct write and must create no tracked change, yet ${nouvelles.length} new tracked change(s) appeared during the action `
        + `(${nouvelles.slice(0, 5).join(', ')}). The lawyer may have typed under review mode at the same time; tell the lawyer, and do not resend the action.`,
    }]),
    ...(faits.pageNonRepeinte !== true ? [] : [{ code: 'FORMAT_PAGE_NOT_REPAINTED', message: `${INTENTION}: ${PAGE_NON_REPEINTE}` }]),
  ];

  const verification: VerificationCheck[] = [
    {
      name: 'format_writes_applied',
      passed: interruption === undefined,
      // Un lot refusé puis rejoué par morceaux compte plus d'écritures qu'il n'en était prévu.
      message: ecritures <= faits.ecrituresPrevues
        ? `${ecritures} of ${faits.ecrituresPrevues} formatting ${mots.ecritures} accepted by the editor, in direct mode.`
        : `${ecritures} formatting ${mots.ecritures} accepted by the editor, in direct mode (${faits.ecrituresPrevues} planned: `
          + 'a batch the editor refused was split and applied without what it refuses).',
    },
    {
      name: 'format_reread_from_exported_file',
      passed: differents.length === 0 && nonRelus.length === 0,
      message: 'Every requested setting must be read back, as requested, from the Word file exported after the write.',
    },
    {
      name: 'format_target_resolved',
      passed: cible.nonResolus.length === 0,
      message: 'Every requested id must designate a paragraph of the document body.',
    },
    {
      name: 'format_every_requested_setting_written',
      passed: laisses.length === 0 && propre.laisseDesReglages !== true,
      message: 'No targeted paragraph may be left with a requested setting unwritten because writing it would erase attributes the editor cannot keep, a pending tracked change, or structure it cannot give back.',
    },
    {
      name: 'format_changed_no_pending_revision',
      passed: surRevision.length === 0,
      message: 'A formatting write must not change what a pending tracked change of paragraph formatting proposes.',
    },
    {
      name: 'format_no_paragraph_skipped',
      passed: sautes.length === 0,
      message: 'No targeted paragraph may be left unwritten because the editor refuses to format it, or because it is spared.',
    },
    {
      name: 'format_reached_every_covered_paragraph',
      passed: horsDePortee === undefined,
      message: 'Every paragraph the target covers must be within reach of the formatting actions: table-cell paragraphs of a header, a footer or a footnote, paragraphs without an identifier and paragraphs that share their identifier are not.',
    },
    {
      name: 'format_page_repainted',
      passed: faits.pageNonRepeinte !== true,
      message: 'The editor must still repaint its page after the writes: a batch it refuses after starting to apply it stops its painter.',
    },
    {
      name: 'no_tracked_change_created',
      passed: nouvelles.length === 0,
      message: 'A formatting write is direct: no tracked-change identity may appear between the reads before and after it.',
    },
    ...(propre.verifications ?? []),
  ];
  const conforme = erreurs.length === 0;
  // Rien d'écrit : rien de neuf que l'avocat puisse relire ou annuler.
  const relisibilite = ecritures === 0 ? 'none' as const : 'direct_untracked' as const;
  const { nomsDesReglages } = propre;
  const paragraphesNommes = paragraphesDistincts(ecrits);
  const nonNommes = paragraphesNommes.length - CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX;
  // Une étape soumise seule et écrite est sa propre annulation : le compte d'écritures en dépend, le reçu le dit.
  const seulesDites = ecritesSeules === 0 ? '' : ` (${ecritesSeules} of them each write one ${selection === undefined ? 'paragraph' : 'passage'} on its own: `
    + 'the editor might have refused it, and a refusal inside a batch stops its page from repainting)';
  const descriptionSansSelection = ecritures === 0
    ? `Nothing ${sautes.length === 0 ? 'to write' : 'written'}: on the ${dejaPoses} targeted paragraph(s)${sautes.length === 0 ? '' : ' the editor accepts'}, writing ${nomsDesReglages} would change nothing `
      + mots.rienAEcrire
      + (sautes.length === 0 ? '' : ` ${sautes.length} more targeted paragraph(s) were skipped (format.skipped).`)
    : `Wrote ${nomsDesReglages} directly (no tracked change) on ${etapesEcrites} paragraph(s), in ${ecritures} ${mots.ecritures}`
      + seulesDites
      + `${nonAnnulables === 0 || annulationIncertaine ? '' : `, of which the first ${nonAnnulables} can no longer be undone (the editor keeps its last ${ecritures - nonAnnulables} writes)`}`
      + `${dejaPoses === 0 ? '' : `; on ${dejaPoses} more, the write would have changed nothing`}`
      + `${sautes.length === 0 ? '' : `; ${sautes.length} more targeted paragraph(s) were skipped (format.skipped)`}`
      + `${nonNommes <= 0 ? '' : `; nodeIds names the first ${CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX}; ${nonNommes} more are written and not named here`}.`;
  // Hors du corps, ni lot ni plan (`horsDuCorps.ts`) : le compte d'écritures ne se lit pas comme dans le corps, l'effet le dit.
  const ecritDansLeLieu = faits.lieu === undefined ? '' : ` All of it in ${direLeLieu(faits.lieu)}, where each paragraph or passage is one write of its own; these nodeIds are not body paragraphs.`;
  // Ce que la règle des tableaux a fait de la cible : dit une fois, en positif, avec la manière de viser ce qui est laissé.
  const cellulesDites = (cellulesEcrites === 0 ? '' : ` ${cellulesEcrites} of the written paragraph(s) are table-cell paragraphs (format.tableCellParagraphsWritten).`)
    + (cellulesLaissees === 0 ? '' : ` ${cellulesLaissees} table-cell paragraph(s) the target covers were left as they are (format.skipped, table_cell_by_ids_only): `
      + 'the layout of a cell belongs to its table. This is the expected result of a request on the whole act or on a range; target their ids only when the lawyer asked for the tables themselves.');
  /** L'effet d'une écriture au grain du passage, et la portée d'un texte visé dans tout l'acte. */
  const descriptionDeLaSelection = (visee: FaitsDeLaSelection, dite: LectureDeLOrigine): string => {
    // Une liste de textes a son compte par texte : l'effet y renvoie, puis dit la portée.
    const portee = dite.partout === undefined ? '' : ` ${'textes' in dite.partout ? 'format.everywhere.texts counts each text. ' : ''}${porteeDuTexteVisePartout(faits.lieu)}`;
    if (visee.trouves.length === 0) return `Nothing to write: ${dite.aucun}. Nothing was written.${portee}`;
    if (ecritures === 0) {
      return `Nothing written on ${dite.dire(visee.trouves.length)}`
        + `${dejaPoses === 0 ? '' : `; on ${dejaPoses}, writing ${nomsDesReglages} would change nothing`}`
        + `${visee.ecartes.length === 0 ? '' : `; ${visee.ecartes.length} were set aside (${dite.champDesSorts}SetAside)`}.${portee}`;
    }
    return `Wrote ${nomsDesReglages} directly (no tracked change) on ${dite.dire(etapesEcrites)}, in ${paragraphesNommes.length} paragraph(s), in ${ecritures} atomic write(s)`
      + seulesDites
      + `${dejaPoses === 0 ? '' : `; on ${dejaPoses} more, the write would have changed nothing`}`
      + `${nonNommes <= 0 ? '' : `; nodeIds names the first ${CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX} paragraphs; ${nonNommes} more are written and not named here`}.${portee}`;
  };

  return {
    intent: INTENTION,
    status: conforme ? 'ok' : 'partial',
    reviewability: relisibilite,
    preSnapshot: faits.avant,
    postSnapshot: faits.apres,
    // Aucune cible nommée : le fichier donne le rang d'un paragraphe parmi ceux
    // du corps, pas l'ordinal de bloc que ce champ exige (un tableau les décale).
    // Les paragraphes écrits sont nommés dans l'effet, comptés dans `format`.
    resolvedTargets: [],
    effects: [{
      kind: INTENTION,
      reviewability: relisibilite,
      description: (selection === undefined || lecture === undefined ? descriptionSansSelection : descriptionDeLaSelection(selection, lecture)) + (propre.suiteDeLEffet ?? '') + cellulesDites + ecritDansLeLieu + annulationDite,
      nodeIds: paragraphesNommes.slice(0, CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX),
    }],
    verification,
    verificationPassed: conforme,
    errors: erreurs,
    format,
    revisionIdentities: { nouvelles, disparues, disparuesInattendues: disparues },
    ...(conforme
      ? {}
      : { recovery: { action: 'stop' as const, reason: 'The receipt names what is not as requested; resending the same action would not change it.' } }),
  };
}
